import classNames from 'classnames'
import { observer } from 'mobx-react-lite'
import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { dmsState } from '@/app/store'
import { type IAddCompanyDto, type IUpdateCompanyDto, type ICompany } from '@/app/types/dmsTypes'
import { onCheckDisabledConditions } from '@/app/utils'
import { routes } from '@Config/config'

import { type IDmsActionType } from '../../../helper'
import { RegularField } from '../../RegularField/RegularField'
import { RemoveModal } from '../../RemoveModal/RemoveModal'
import Vector from '../../Vector.svg'

import style from './Data.module.scss'
import { useFields } from './useFields'

interface IProps {
    type: string
}

export const Data = observer((props: IProps) => {
    const { type } = props
    const { companyId } = useParams()
    const navi = useNavigate()

    const [isRemovableCompany, setIsRemovableCompany] = useState<boolean>(false)

    if (!dmsState.chosenCompany) {
        return null
    }
    const data: ICompany = Object.values(dmsState.chosenCompany)[0]

    const { inn, onChangeInn, onSetIsTouchedInn, name, onChangeName, onSetIsTouchedName, isSomeToched, onSetTouchAll } =
        useFields({ data })

    const addCompanyDto: IAddCompanyDto = {
        name: name.value || '',
        inn: inn.value
    }

    const changeCompanyDto: IUpdateCompanyDto = {
        id: companyId || '',
        name: name.value || '',
        inn: inn.value
    }
    const onRemoveCompanyHandler = () => {
        if (data?.id) {
            dmsState.onRemoveCompany(data.id, () => {
                dmsState.onSetChosenCompany(null)
                setIsRemovableCompany(false)
                navi(`${routes.DMSM}/companies`)
            })
        }
    }

    const onAddCompanyHandler = () => {
        dmsState.onAddCompany(addCompanyDto, (newCompanyId: string) => {
            dmsState.onSetChosenCompany(null)
            navi(`${routes.DMSM}/companies/${newCompanyId}`)
        })
    }

    const onCancelAddition = () => {
        dmsState.onSetChosenCompany(null)
        navi(-1)
    }

    const onSaveCompanyChangesHandler = () => {
        dmsState.onSaveCompanyChanges(changeCompanyDto, () => {})
    }

    const onGetInsuranseCompanyContracts = () => {
        if (companyId) {
            // onGetCompanyContracts(data.id)
            navi(`${routes.DMSM}/companies/${companyId}/contracts`)
        }
    }

    const actions: Record<string, IDmsActionType[]> = {
        add: [
            {
                title: 'Сохранить',
                type: 'create',
                callback: onAddCompanyHandler,
                isDisabledRequired: true
            },
            {
                title: 'Отмена',
                type: 'remove',
                callback: onCancelAddition,
                isDisabledRequired: false
            }
        ],
        edit: [
            {
                title: 'Договоры с СК',
                type: 'contracts',
                callback: onGetInsuranseCompanyContracts,
                isDisabledRequired: false,
                hasMarker: data.completionSoon
            },
            {
                title: 'Сохранить изменения',
                type: 'update',
                callback: onSaveCompanyChangesHandler,
                isDisabledRequired: true
            },
            {
                title: 'Удалить компанию',
                type: 'remove',
                callback: () => setIsRemovableCompany(true),
                isDisabledRequired: false
            }
        ]
    }

    const isValidFields = onCheckDisabledConditions(Boolean(!inn.error), Boolean(!name.error))

    return (
        <div className={style.container}>
            <div className={style.wrapper}>
                <div className={style.insuranceCompanyInn}>
                    <RegularField entity={inn} onChange={onChangeInn} onSetIsTouched={onSetIsTouchedInn} />
                </div>
                <div className={style.insuranceCompanyName}>
                    <RegularField entity={name} onChange={onChangeName} onSetIsTouched={onSetIsTouchedName} />
                </div>
            </div>
            <div className={style.actions}>
                {actions[type].map(({ title, type, callback, isDisabledRequired, hasMarker }) => {
                    const onClick = () => {
                        if (!isValidFields || isSomeToched || !isDisabledRequired) {
                            callback()
                        } else {
                            onSetTouchAll()
                        }
                    }
                    const onSetDisabledMode = (): boolean => {
                        if (isDisabledRequired) {
                            if (!isSomeToched) {
                                return false
                            }
                            if (!isValidFields) {
                                return false
                            }
                            return true
                        }
                        return false
                    }
                    return (
                        <button
                            key={title}
                            className={classNames(style.btn, style[type])}
                            onClick={onClick}
                            disabled={onSetDisabledMode()}
                        >
                            {title}
                            {hasMarker && (
                                <div style={{ display: 'flex' }} title='Договор между СК и ЛПУ скоро завершится'>
                                    <Vector />
                                </div>
                            )}
                        </button>
                    )
                })}
            </div>
            {isRemovableCompany && (
                <RemoveModal
                    entity={data.name}
                    headerTitle='Вы уверены, что хотите удалить страховую компанию?'
                    onClose={() => setIsRemovableCompany(false)}
                    onRemoveCallback={onRemoveCompanyHandler}
                />
            )}
        </div>
    )
})
